// BuyChallenge.tsx
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import BridgerPayWidget from '../../components/PaymentWidget/BridgerPayWidget';
import { initiateBridgerPayment } from '../../services/payment/bridgerPay';
import Cookies from 'js-cookie';

import { useThemeContext } from '../../context/ThemeContext/ThemeContext';
import { useAuth } from '../../context/AuthContext/AuthContext';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import {
  fetchShop,
  fetchShopConnected,
  applyPromoCode,
} from '../../services/shop/shop';
import Loader from '../../components/Loader/Loader';
import axios from 'axios';

import classes from './_buyChallenge.module.scss';
import { toast } from 'react-toastify';
import {
  Box,
  Button,
  Card,
  IconButton,
  Modal,
  Stack,
  Typography,
} from '@mui/material';
import PixelBuyChallenge from '../../utils/pixel/PixelPageView';
import PixelPageView from '../../utils/pixel/PixelPageView';

import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import CloseIcon from '@mui/icons-material/Close';
import { trackAddToCart } from '../../utils/gtm/GTMService';

import BuyChallengeForm from './BuyChallengeForm';
import ChallengeConditionsTable from './ChallengeConditionsTable';
import BuyChallengeHeader from './BuyChallengeHeader';
import BuyChallengeFooter from './BuyChallengeFooter';
import ChallengeCard from './ChallengeCard';
import { usePromoTimer } from './usePromoTimer';
import { useAllowedCountries } from './useAllowedCountries';
import { useWalletDeduction } from './useWalletDeduction';
import { shouldRedirectToWebsite } from '../../services/affiliation/affiliation';
import { useNavigate } from 'react-router-dom';

export interface DiscountInfo {
  discountValue: number;
  discountPercentage: number;
  finalPrice: number;
}

interface QuantityItem {
  quantity: number;
  price: number;
  originalPrice: number;
}

interface Quantities {
  [key: string]: QuantityItem;
}

interface Challenge {
  challengeBalance: number;
  initialPrice: number;
  currentPrice: number;
}

const BuyChallenge = () => {
  useDocumentTitle('New Challenge');

  const { t } = useTranslation(['buyChallenge', 'common', 'challengesCards']);
  const { isDarkMode } = useThemeContext();
  const { tokens, isTokensLoading } = useAuth();
  const { getCode } = require('country-list');

  const prevTokenRef = useRef<string | null>(null);
  const location = window.location;
  const referredBy = new URLSearchParams(location.search).get('affiliate');
  const [showPaymentWidget, setShowPaymentWidget] = useState(false);
  const [bridgerSession, setBridgerSession] = useState<{
    externalId: string;
    cashierKey: string;
    cashierToken: string;
  } | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [multiplierFactors, setMultiplierFactors] = useState<any>();
  const [user, setUser] = useState<any>();
  const [discountInfo, setDiscountInfo] = useState<DiscountInfo | null>(null);
  const [quantities, setQuantities] = useState<Quantities>({});
  const [useVirtualWallet, setUseVirtualWallet] = useState(false);
  const [phone, setPhoneValue] = useState('');
  const [phoneIsValid, setPhoneIsValid] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<string | undefined>('');
  const [selectedPayment, setSelectedPayment] = useState<string | undefined>(
    t('billingInfoCard.creditCard'),
  );
  const [phoneError, setPhoneError] = useState<string>('');
  const navigate = useNavigate();

  const [showPopup, setShowPopup] = useState(false);
  const [pendingQuantityChange, setPendingQuantityChange] = useState<number | null>(null);

  const [promoCode, setPromoCode] = useState('');
  const [promoError, setPromoError] = useState('');

  const { activePromo, promoTimeLeft, formatTime } = usePromoTimer();
  const countryOptions = useAllowedCountries();

  const quantity100kInCart = quantities['100000USD']?.quantity || 0;
  const isBlackFriday100kActive =
    activePromo === 'blackFriday' &&
    promoTimeLeft > 0 &&
    quantity100kInCart > 0;

  // Max 2 challenges 100k profitent de l'offre (2 offerts max)
  const eligibleOffers = isBlackFriday100kActive
    ? Math.min(quantity100kInCart, 2)
    : 0;

  const free100kChallenges = eligibleOffers;
  const total100kReceived = quantity100kInCart + free100kChallenges;

  const blackFriday100kInfo = isBlackFriday100kActive
    ? {
        quantityInCart: quantity100kInCart,
        freeChallenges: free100kChallenges,
        totalReceived: total100kReceived,
      }
    : null;

  useEffect(() => {
    if (!referredBy) return;

    try {
      localStorage.setItem('affiliate', referredBy);
      Cookies.set('affiliate', referredBy, { expires: 30 });
    } catch (error) {
      console.error('Error while saving affiliate code:', error);
    }

    const redirectMap: Record<string, string> = {
      OffersRMF: 'https://offers.raisemyfunds.co/fr',
      rmf: 'https://raisemyfunds.co',
      GoogleAds: 'https://start.raisemyfunds.co/',
    };

    const targetUrl = redirectMap[referredBy];

    if (targetUrl) {
      const redirectFlagKey = `affiliate_redirect_${referredBy}`;
      const alreadyRedirected = localStorage.getItem(redirectFlagKey);

      if (!alreadyRedirected) {
        try {
          localStorage.setItem(redirectFlagKey, 'true');
        } catch (error) {
          console.error('Error while saving affiliate redirect flag:', error);
        }
        window.location.href = targetUrl;
      }
    }
  }, [referredBy]);


  const calculateSubtotal = useCallback(() => {
    return Object.entries(quantities).reduce(
      (sum, [_, { quantity, price }]) => {
        const numQuantity = Number(quantity);
        const numPrice = Number(price);
        return (
          sum +
          (isNaN(numQuantity) || isNaN(numPrice) ? 0 : numQuantity * numPrice)
        );
      },
      0,
    );
  }, [quantities]);

  const walletDeduction = useWalletDeduction(
    useVirtualWallet,
    user?.currentWallet,
    calculateSubtotal,
    discountInfo,
  );

  const handleButtonClick = () => {
    setUseVirtualWallet(true);
  };

  function isValidPhoneNumber(phoneNumber: string): boolean {
    const cleanedPhoneNumber = phoneNumber.replace(/[\s-.]/g, '');
    const regex = /^\+[1-9]\d{1,14}$/;

    if (cleanedPhoneNumber.length < 9) {
      return false;
    }

    return regex.test(cleanedPhoneNumber);
  }

    const [isRedirectChecking, setIsRedirectChecking] = useState<boolean>(true);

  useEffect(() => {
    const runRedirectCheck = async () => {
      if (!tokens) {
        const params = new URLSearchParams(window.location.search);
        const affiliate = params.get('affiliate');

        if (affiliate) {
          try {
            const shouldRedirect = await shouldRedirectToWebsite(affiliate);

            Cookies.set('affiliate', affiliate, { expires: 30 });
            localStorage.setItem('affiliate', affiliate);

            if (shouldRedirect) {
              window.location.href = `https://raisemyfunds.co${window.location.search}`;
              return; // stop execution if redirected
            } 
          } catch (error) {
            console.error('Erreur affiliation:', error);
          }
        }
      }

      setIsRedirectChecking(false); // Fin de vérification
    };

    runRedirectCheck();
  }, [tokens, navigate]);


  useEffect(() => {
    if (user?.phoneNumber) {
      setPhoneValue(user.phoneNumber);
      setPhoneIsValid(true);
    }
  }, [user?.phoneNumber]);

  const handlePhoneChange = (newValue: string) => {
    if (isValidPhoneNumber(newValue)) {
      setPhoneError('');
      setPhoneIsValid(true);
    } else {
      setPhoneError(t('billingInfoCard.PLEASE_ENTER_A_VALID_PHONE_NUMBER'));
      setPhoneIsValid(false);
    }

    setPhoneValue(newValue);
  };

  const generateItemsList = () => {
    const items: { balance: number; quantity: number; price: number }[] = [];
  
    Object.keys(quantities).forEach((key) => {
      const { quantity, price, originalPrice } = quantities[key];
      const balance = parseInt(key.replace('USD', ''), 10);
  
      if (balance === 10000) {
        if (quantity > 0) {
          items.push({ balance, quantity: 1, price });
          if (quantity > 1) {
            items.push({ balance, quantity: quantity - 1, price: originalPrice });
          }
        }
      } else {
        items.push({ balance, quantity, price });
      }
    });
  
    return items;
  };
  
  const preparePaymentData = (event: any) => {
    const items = generateItemsList();
  
    const formData = new FormData(event.target);
    const firstName = formData.get('firstname');
    const lastName = formData.get('lastname');
    const email = formData.get('email');
    const country = selectedOption;
    const city = formData.get('city');
    const street = formData.get('street');
    const zipCode = formData.get('postalCode');
  
    return {
      items,
      email,
      promoCode: promoCode,
      phoneNumber: user?.phoneNumber || phone,
      firstname: firstName,
      lastname: lastName,
      country: country || user?.country || 'Albania',
      zipCode: zipCode,
      city: city,
      street: street,
      ipAddress: user?.ipAddress,
      referredBy: localStorage.getItem('affiliate') || Cookies.get('affiliate'),
      role: 'ROLE_USER',
      useVirtualWallet: useVirtualWallet,
    };
  };

  const updateStates = (data: any) => {
    setChallenges(data.challenges);
    setMultiplierFactors(data);
    setUser(data);
    const initialQuantities = data.challenges.reduce(
      (acc: any, challenge: any) => {
        acc[`${challenge.challengeBalance}USD`] = {
          quantity: 0,
          price: challenge.currentPrice,
          originalPrice:
            challenge.currentPrice < challenge.initialPrice
              ? challenge.currentPrice
              : challenge.initialPrice,
        };
        return acc;
      },
      {},
    );
    setQuantities(initialQuantities);
  };

  const handlePayment = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const hasSelectedItems = Object.values(quantities).some(
      (item) => item.quantity > 0,
    );
    if (!hasSelectedItems) {
      toast.info(t('billingInfoCard.PLEASE_SELECT_AT_LEAST_ONE_ARTICLE'), {
        progressStyle: { backgroundColor: 'cornflowerblue' },
      });
      return;
    }

    if (!phoneIsValid) {
      toast.error(t('billingInfoCard.INVALID_PHONE_NUMBER'), {
        progressStyle: { backgroundColor: 'red' },
      });
      return;
    }

    try {
      const paymentData = preparePaymentData(event);

      if (selectedPayment === t('billingInfoCard.cryptoWallet')) {
        const response = await axios.post(
          'https://live.raisemyfunds.co/api/shop/initPayment',
          paymentData,
        );

        if (response.data.paymentPage) {
          const { paymentPage, callbackId } = response.data;

          if (paymentPage && callbackId) {
            window.open(paymentPage, '_blank', 'noopener,noreferrer');

            window.location.assign(`/buy-challenge/payment-status/${callbackId}`);
          }
        } else if (response.data.message === 'Payment completed using wallet') {
          window.location.href = `/buy-challenge/payment-status/${response.data.callbackId}`;
        }
      } else {
        // Credit / Debit Card & Apple Pay → BridgerPay cashier widget.
        const data = await initiateBridgerPayment(paymentData);

        // Virtual wallet fully covered the amount: the backend already settled
        // the deposit and returned a callbackId instead of a cashier session.
        if (!data.cashierToken && data.callbackId) {
          window.location.href = `/buy-challenge/payment-status/${data.callbackId}`;
          return;
        }

        // Persist buyer details for the payment-status page (guest tracking
        // fallback + post-payment account creation), mirroring the old flow.
        const { email, firstname, lastname, phoneNumber, street, city, zipCode, country } =
          paymentData;
        localStorage.setItem(
          'paymentInfo',
          JSON.stringify({
            finalAmount: data.finalAmount,
            externalId: data.externalId,
            email,
            firstname,
            lastname,
            phoneNumber,
            birthDate: data.birthDate,
            city,
            street,
            country,
            zipCode,
          }),
        );

        setBridgerSession({
          externalId: data.externalId as string,
          cashierKey: data.cashierKey as string,
          cashierToken: data.cashierToken as string,
        });
        setShowPaymentWidget(true);
      }
    } catch (error: any) {
      if (error.response?.status === 400) {
        toast.error(
          t('billingInfoCard.CANNOT_EXCEED_TOTAL_FUNDING_500K_FROM_CHALLENGES'),
          {
            progressStyle: { backgroundColor: 'red' },
          },
        );
      } else if (error.response?.status === 409) {
        toast.error(t('billingInfoCard.THIS_PHONE_NUMBER_IS_ALREADY_USE'), {
          progressStyle: { backgroundColor: 'red' },
        });
      } else {
        console.error("Erreur lors de l'initiation du paiement: ", error);
      }
    }
  };

  const handlePromoCodeChange = (e: any) => {
    setPromoCode(e.target.value);
  };

  const calculateTotalCost = () => {
    const totalCost = Object.entries(quantities).reduce((sum, [key, { quantity }]) => {
      const challenge = challenges.find((ch) => `${ch.challengeBalance}USD` === key);
      if (!challenge) return sum;
  
      const isFirstChallenge = key === `${challenges[0]?.challengeBalance}USD`;
  
      if (isFirstChallenge) {
        const firstUnitPrice = quantity >= 1 ? challenges[0].currentPrice : 0;
        const additionalUnitsPrice = quantity > 1 ? (quantity - 1) * challenges[0].initialPrice : 0;
        return sum + firstUnitPrice + additionalUnitsPrice;
      } else {
        const priceToUse =
          challenge.currentPrice < challenge.initialPrice
            ? challenge.currentPrice
            : challenge.initialPrice;
        return sum + quantity * priceToUse;
      }
    }, 0);
  
    const discountedPrice = discountInfo ? discountInfo.finalPrice : totalCost;
  
    return useVirtualWallet ? discountedPrice - walletDeduction : discountedPrice;
  };
  

  const handleApplyPromoCode = async () => {
    if (!promoCode) {
      setPromoError(t('billingInfoCard.enterPromoCodeError'));
      return;
    }
  
    const items = generateItemsList();
  
    try {
      let response;
      if (!tokens || !tokens.accessToken) {
        response = await applyPromoCode(
          null,
          promoCode,
          items,
          referredBy
        );
      } else {
        response = await applyPromoCode(
          tokens.accessToken,
          promoCode,
          items,
          null
        );
      }
      setDiscountInfo(response);
      setPromoError('');
    } catch (error) {
      setPromoError(t('billingInfoCard.failedToApplyPromoCode'));
      setDiscountInfo(null);
    }
  };
  
  const fetchData = async () => {
    setIsLoading(true);
    try {
      let data;
      if (tokens) {
        prevTokenRef.current = tokens.accessToken;
        data = await fetchShopConnected(tokens.accessToken);
      } else {
        data = await fetchShop();
      }
      updateStates(data);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isTokensLoading) {
      fetchData();
    }
  }, [tokens, isTokensLoading]);

  const handleQuantityChange = (productKey: string, newQuantity: number) => {
    let popupTriggered = false;
  
    const prevQuantity = quantities[productKey]?.quantity || 0;

    if (newQuantity > prevQuantity) {
      const challenge = challenges.find(ch => `${ch.challengeBalance}USD` === productKey);
      if (challenge) {
        trackAddToCart({
          value: challenge.currentPrice,
          currency: 'EUR',
          items: [
            {
              item_id: `CHALLENGE_${challenge.challengeBalance}`,
              item_name: `CHALLENGE_${challenge.challengeBalance}`,
              price: challenge.currentPrice,
              quantity: 1,
              affiliation: localStorage.getItem('affiliate') || undefined,
              coupon: promoCode || undefined,
              discount: discountInfo?.discountValue || undefined,
            }
          ]
        });
      }
    }

    setQuantities((prevQuantities) => {
      const updatedQuantities = { ...prevQuantities };
      const firstChallengeKey = `${challenges[0]?.challengeBalance}USD`;
  
      if (productKey === firstChallengeKey) {
        if (
          challenges[0].currentPrice === 85 &&
          prevQuantities[firstChallengeKey].quantity === 1 &&
          newQuantity === 2
        ) {
          setPendingQuantityChange(newQuantity);
          setShowPopup(true);
          popupTriggered = true;
          return prevQuantities;
        }
        updatedQuantities[productKey] = {
          ...updatedQuantities[productKey],
          quantity: newQuantity,
          price:
            newQuantity === 1
              ? challenges[0].currentPrice
              : challenges[0].initialPrice,
        };
      } else {
        const challenge = challenges.find(
          (ch) => `${ch.challengeBalance}USD` === productKey,
        );
        if (challenge) {
          updatedQuantities[productKey] = {
            ...updatedQuantities[productKey],
            quantity: newQuantity,
            price:
              challenge.currentPrice < challenge.initialPrice
                ? challenge.currentPrice
                : challenge.initialPrice,
          };
        }
      }
  
      return updatedQuantities;
    });
  
    if (!popupTriggered) {
      setDiscountInfo(null);
    }
  };
  

  const paymentOptions =
    user?.country === 'Algeria' || selectedOption === 'Algeria'
      ? [t('billingInfoCard.cryptoWallet')]
      : [t('billingInfoCard.creditCard'), t('billingInfoCard.cryptoWallet')];

  const handleConfirmAdditionalChallenge = () => {
    if (pendingQuantityChange !== null) {
      const firstChallengeKey = `${challenges[0]?.challengeBalance}USD`;
      setQuantities((prevQuantities) => {
        const updatedQuantities = { ...prevQuantities };
        updatedQuantities[firstChallengeKey] = {
          ...updatedQuantities[firstChallengeKey],
          quantity: pendingQuantityChange,
          price: challenges[0].initialPrice,
        };
        return updatedQuantities;
      });
      setShowPopup(false);
      setPendingQuantityChange(null);
      setDiscountInfo(null);
    }
  };

  const handleCancelAdditionalChallenge = () => {
    handleConfirmAdditionalChallenge();
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      {showPaymentWidget && bridgerSession && (
        <Modal open={showPaymentWidget} onClose={() => {}}>
          <Box
            sx={{
              position: 'fixed',
              inset: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              p: 2,
            }}
          >
            <Box
              onClick={(e) => e.stopPropagation()}
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: 500,
                maxHeight: '90vh',
                overflowY: 'auto',
                p: 3,
                bgcolor: '#111217',
                borderRadius: '16px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
              }}
            >
              <IconButton
                onClick={() => {
                  setShowPaymentWidget(false);
                  setBridgerSession(null);
                }}
                sx={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 2,
                  width: 32,
                  height: 32,
                  color: '#fff',
                  bgcolor: 'rgba(0, 0, 0, 0.55)',
                  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.4)',
                  '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.75)' },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>

              <BridgerPayWidget
                open={showPaymentWidget}
                defaultMethod="card"
                cashierKey={bridgerSession.cashierKey}
                cashierToken={bridgerSession.cashierToken}
                externalId={bridgerSession.externalId}
                onClose={() => {
                  setShowPaymentWidget(false);
                  setBridgerSession(null);
                }}
                onResult={(externalId) => {
                  // Route to the status page on ANY outcome (success or failure);
                  // that page polls the backend for the real payment status.
                  window.location.href = `/buy-challenge/payment-status/${externalId}`;
                }}
              />
            </Box>
          </Box>
        </Modal>
      )}
      <PixelBuyChallenge />
      <PixelPageView />

      {!tokens && <BuyChallengeHeader />}

      <div className={classes.mainContainer}>
        <div className={classes.challengeContainer}>
          {challenges.map((challenge, index) => (
            <ChallengeCard
              key={challenge.challengeBalance}
              t={t}
              isDarkMode={isDarkMode}
              challenge={challenge}
              index={index}
              isLast={index === challenges.length - 1}
              multiplierFactors={multiplierFactors}
              quantities={quantities}
              onQuantityChange={handleQuantityChange}
              activePromo={activePromo}
              promoTimeLeft={promoTimeLeft}
              formatTime={formatTime}
            />
          ))}

          <Card
            elevation={0}
            sx={{ padding: '16px' }}
            className={classes.infoCard}
          >
            <div className={classes.iconContainer}>
              <MonetizationOnIcon sx={{ fontSize: '42px' }} />
            </div>
            <div className={classes.content}>
              <h2>{t('useWalletCard.useWalletTitle')}</h2>
              <h3 className={classes.subtitle}>
                {tokens
                  ? t('useWalletCard.useWalletSubtitle', {
                      money: user?.currentWallet,
                    })
                  : ''}
              </h3>
              <p>{t('useWalletCard.useWalletText')}</p>
            </div>

            <Button
              variant="contained"
              disabled={!tokens}
              onClick={handleButtonClick}
            >
              {t('useWalletCard.useWalletButton')}
            </Button>
          </Card>
        </div>

        <BuyChallengeForm
          t={t}
          user={user}
          tokens={tokens}
          countryOptions={countryOptions}
          selectedOption={selectedOption}
          setSelectedOption={setSelectedOption}
          phone={phone}
          phoneError={phoneError}
          handlePhoneChange={handlePhoneChange}
          selectedPayment={selectedPayment}
          setSelectedPayment={setSelectedPayment}
          paymentOptions={paymentOptions}
          promoCode={promoCode}
          promoError={promoError}
          handlePromoCodeChange={handlePromoCodeChange}
          handleApplyPromoCode={handleApplyPromoCode}
          calculateTotalCost={calculateTotalCost}
          discountInfo={discountInfo}
          useVirtualWallet={useVirtualWallet}
          setUseVirtualWallet={setUseVirtualWallet}
          walletDeduction={walletDeduction}
          handlePayment={handlePayment}
          getCode={getCode}
          blackFriday100kInfo={blackFriday100kInfo}
        />
      </div>

      <ChallengeConditionsTable t={t} isDarkMode={isDarkMode} />

      {!tokens && <BuyChallengeFooter t={t} />}

      <Modal open={showPopup} onClose={handleCancelAdditionalChallenge}>
        <Box
          sx={{
            position: 'absolute' as const,
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'background.paper',
            borderRadius: '8px',
            boxShadow: 24,
            p: 4,
            maxWidth: '400px',
            width: '90%',
          }}
        >
          <Typography variant="h6" component="h2" mb={2}>
            {t('challengesCards.FIRST_10K_MODAL_TITLE')}
          </Typography>
          <Typography variant="body1" component={'p'} mb={3}>
            {t('challengesCards.FIRST_10_MODAL_TEXT')}
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="contained"
              onClick={handleConfirmAdditionalChallenge}
            >
              {t('challengesCards.FIRST_10K_MODAL_BUTTON')}
            </Button>
          </Stack>
        </Box>
      </Modal>
    </>
  );
};

export default BuyChallenge;
